import { create } from 'zustand';
import { api } from './api';

const useStore = create((set, get) => ({
  // Services
  services: [],
  servicesLoading: false,
  selectedService: null,
  serviceOperations: {},
  serviceOperationsLoading: {},
  serviceConfigs: {},
  serviceConfigsLoading: {},
  selectedOperation: null,
  selectedConfig: null,

  // Endpoints
  endpoints: [],
  endpointsLoading: false,
  selectedEndpoint: null,

  // Requests
  requests: [],
  requestsLoading: false,
  
  error: null,

  // Service Actions
  fetchServices: async () => {
    try {
      const state = get();
      if (state.servicesLoading) {
        return;
      }

      set({ servicesLoading: true, error: null });
      const services = await api.getServices();
      set({ services, servicesLoading: false, error: null });
    } catch (error) {
      console.error('Error fetching services:', error);
      set({ 
        error: error.message || 'Failed to fetch services', 
        servicesLoading: false,
        services: [] 
      });
      throw error; // Propagate error to allow component-level handling
    }
  },

  createService: async (data) => {
    try {
      set({ servicesLoading: true, error: null });
      const newService = await api.createService(data);
      set(state => ({
        services: [...(state.services || []), newService],
        servicesLoading: false,
        error: null
      }));
      return newService;
    } catch (error) {
      console.error('Error creating service:', error);
      set({ 
        error: error.message || 'Failed to create service', 
        servicesLoading: false 
      });
      throw error; // Propagate error to allow component-level handling
    }
  },

  updateService: async (id, data) => {
    try {
      set({ servicesLoading: true, error: null });
      const updatedService = await api.updateService(id, data);
      set(state => ({
        services: (state.services || []).map(service =>
          service.id === id ? updatedService : service
        ),
        servicesLoading: false,
        error: null
      }));
      return updatedService;
    } catch (error) {
      console.error('Error updating service:', error);
      set({ 
        error: error.message || 'Failed to update service', 
        servicesLoading: false 
      });
      throw error; // Propagate error to allow component-level handling
    }
  },

  deleteService: async (id) => {
    try {
      set({ servicesLoading: true, error: null });
      await api.deleteService(id);
      set(state => ({
        services: (state.services || []).filter(service => service.id !== id),
        serviceOperations: { ...state.serviceOperations, [id]: undefined },
        serviceConfigs: { ...state.serviceConfigs, [id]: undefined },
        servicesLoading: false,
        error: null
      }));
    } catch (error) {
      console.error('Error deleting service:', error);
      set({ 
        error: error.message || 'Failed to delete service', 
        servicesLoading: false 
      });
      throw error; // Propagate error to allow component-level handling
    }
  },

  setSelectedService: (service) => {
    set({ selectedService: service });
  },

  // Operations
  fetchServiceOperations: async (serviceId) => {
    try {
      const state = get();
      if (state.serviceOperationsLoading?.[serviceId]) {
        return;
      }

      // First check if the service exists
      const service = state.services.find(s => s.id === serviceId);
      if (!service) {
        throw new Error('Service not found');
      }

      set(state => ({
        serviceOperationsLoading: {
          ...state.serviceOperationsLoading,
          [serviceId]: true
        },
        error: null
      }));

      const operations = await api.getServiceOperations(serviceId);
      
      // Always set operations, even if empty
      set(state => ({
        serviceOperations: {
          ...state.serviceOperations,
          [serviceId]: operations || []
        },
        serviceOperationsLoading: {
          ...state.serviceOperationsLoading,
          [serviceId]: false
        },
        error: null
      }));

      // Return operations to allow component to check length
      return operations || [];
    } catch (error) {
      console.error('Error fetching service operations:', error);
      set(state => ({
        serviceOperations: {
          ...state.serviceOperations,
          [serviceId]: [] // Set empty array on error
        },
        serviceOperationsLoading: {
          ...state.serviceOperationsLoading,
          [serviceId]: false
        },
        error: error.message === 'Service not found' ? error.message : 'Failed to fetch service operations'
      }));
      throw error;
    }
  },

  createOperation: async (serviceId, data) => {
    try {
      set({ servicesLoading: true, error: null });
      const newOperation = await api.createServiceOperation(serviceId, data);
      set(state => ({
        serviceOperations: {
          ...state.serviceOperations,
          [serviceId]: [
            ...(state.serviceOperations[serviceId] || []),
            newOperation
          ]
        },
        servicesLoading: false,
        error: null
      }));
      return newOperation;
    } catch (error) {
      console.error('Error creating operation:', error);
      set({ 
        error: error.message || 'Failed to create operation', 
        servicesLoading: false 
      });
      throw error; // Propagate error to allow component-level handling
    }
  },

  updateOperation: async (serviceId, operationId, data) => {
    try {
      set({ servicesLoading: true, error: null });
      const updatedOperation = await api.updateServiceOperation(serviceId, operationId, data);
      set(state => ({
        serviceOperations: {
          ...state.serviceOperations,
          [serviceId]: state.serviceOperations[serviceId].map(op =>
            op.id === operationId ? updatedOperation : op
          )
        },
        servicesLoading: false,
        error: null
      }));
      return updatedOperation;
    } catch (error) {
      console.error('Error updating operation:', error);
      set({ 
        error: error.message || 'Failed to update operation', 
        servicesLoading: false 
      });
      throw error; // Propagate error to allow component-level handling
    }
  },

  deleteOperation: async (serviceId, operationId) => {
    try {
      set({ servicesLoading: true, error: null });
      await api.deleteServiceOperation(serviceId, operationId);
      set(state => ({
        serviceOperations: {
          ...state.serviceOperations,
          [serviceId]: state.serviceOperations[serviceId].filter(
            op => op.id !== operationId
          )
        },
        servicesLoading: false,
        error: null
      }));
    } catch (error) {
      console.error('Error deleting operation:', error);
      set({ 
        error: error.message || 'Failed to delete operation', 
        servicesLoading: false 
      });
      throw error; // Propagate error to allow component-level handling
    }
  },

  setSelectedOperation: (operation) => {
    set({ selectedOperation: operation });
  },

  // Configs
  fetchServiceConfigs: async (serviceId) => {
    try {
      const state = get();
      if (state.serviceConfigsLoading?.[serviceId]) {
        return;
      }

      set(state => ({
        serviceConfigsLoading: {
          ...state.serviceConfigsLoading,
          [serviceId]: true
        },
        error: null
      }));

      const configs = await api.getServiceConfigs(serviceId);
      
      // If the service was deleted, the backend will return an empty list
      // In this case, we should mark it as an error
      if (!configs || configs.length === 0) {
        throw new Error('Service not found or has no configs');
      }

      set(state => ({
        serviceConfigs: {
          ...state.serviceConfigs,
          [serviceId]: configs
        },
        serviceConfigsLoading: {
          ...state.serviceConfigsLoading,
          [serviceId]: false
        },
        error: null
      }));
    } catch (error) {
      console.error('Error fetching service configs:', error);
      set(state => ({
        error: error.message || 'Failed to fetch service configs',
        serviceConfigsLoading: {
          ...state.serviceConfigsLoading,
          [serviceId]: false
        }
      }));
      throw error; // Propagate error to allow component-level handling
    }
  },

  createConfig: async (serviceId, data) => {
    try {
      set({ servicesLoading: true, error: null });
      const newConfig = await api.createServiceConfig(serviceId, data);
      set(state => ({
        serviceConfigs: {
          ...state.serviceConfigs,
          [serviceId]: [
            ...(state.serviceConfigs[serviceId] || []),
            newConfig
          ]
        },
        servicesLoading: false,
        error: null
      }));
      return newConfig;
    } catch (error) {
      console.error('Error creating config:', error);
      set({ 
        error: error.message || 'Failed to create config', 
        servicesLoading: false 
      });
      throw error; // Propagate error to allow component-level handling
    }
  },

  updateConfig: async (serviceId, configId, data) => {
    try {
      set({ servicesLoading: true, error: null });
      const updatedConfig = await api.updateServiceConfig(serviceId, configId, data);
      set(state => ({
        serviceConfigs: {
          ...state.serviceConfigs,
          [serviceId]: state.serviceConfigs[serviceId].map(config =>
            config.id === configId ? updatedConfig : config
          )
        },
        servicesLoading: false,
        error: null
      }));
      return updatedConfig;
    } catch (error) {
      console.error('Error updating config:', error);
      set({ 
        error: error.message || 'Failed to update config', 
        servicesLoading: false 
      });
      throw error; // Propagate error to allow component-level handling
    }
  },

  deleteConfig: async (serviceId, configId) => {
    try {
      set({ servicesLoading: true, error: null });
      await api.deleteServiceConfig(serviceId, configId);
      set(state => ({
        serviceConfigs: {
          ...state.serviceConfigs,
          [serviceId]: state.serviceConfigs[serviceId].filter(
            config => config.id !== configId
          )
        },
        servicesLoading: false,
        error: null
      }));
    } catch (error) {
      console.error('Error deleting config:', error);
      set({ 
        error: error.message || 'Failed to delete config', 
        servicesLoading: false 
      });
      throw error; // Propagate error to allow component-level handling
    }
  },

  setSelectedConfig: (config) => {
    set({ selectedConfig: config });
  },

  // Endpoints
  fetchEndpoints: async () => {
    try {
      const state = get();
      if (state.endpointsLoading) {
        return;
      }

      set({ endpointsLoading: true, error: null });
      
      const endpoints = await api.getEndpoints();
      
      set({ endpoints, endpointsLoading: false, error: null });
    } catch (error) {
      console.error('Error fetching endpoints:', error);
      set({ 
        error: error.message || 'Failed to fetch endpoints', 
        endpointsLoading: false 
      });
      throw error; // Propagate error to allow component-level handling
    }
  },

  createEndpoint: async (data) => {
    try {
      set({ endpointsLoading: true, error: null });
      const newEndpoint = await api.createEndpoint(data);
      set(state => ({
        endpoints: [...state.endpoints, newEndpoint],
        endpointsLoading: false,
        error: null
      }));
      return newEndpoint;
    } catch (error) {
      console.error('Error creating endpoint:', error);
      set({ 
        error: error.message || 'Failed to create endpoint', 
        endpointsLoading: false 
      });
      throw error; // Propagate error to allow component-level handling
    }
  },

  updateEndpoint: async (id, data) => {
    try {
      set({ endpointsLoading: true, error: null });
      const updatedEndpoint = await api.updateEndpoint(id, data);
      set(state => ({
        endpoints: state.endpoints.map(endpoint =>
          endpoint.id === id ? updatedEndpoint : endpoint
        ),
        endpointsLoading: false,
        error: null
      }));
      return updatedEndpoint;
    } catch (error) {
      console.error('Error updating endpoint:', error);
      set({ 
        error: error.message || 'Failed to update endpoint', 
        endpointsLoading: false 
      });
      throw error; // Propagate error to allow component-level handling
    }
  },

  deleteEndpoint: async (id) => {
    try {
      set({ endpointsLoading: true, error: null });
      await api.deleteEndpoint(id);
      set(state => ({
        endpoints: state.endpoints.filter(endpoint => endpoint.id !== id),
        endpointsLoading: false,
        error: null
      }));
    } catch (error) {
      console.error('Error deleting endpoint:', error);
      set({ 
        error: error.message || 'Failed to delete endpoint', 
        endpointsLoading: false 
      });
      throw error; // Propagate error to allow component-level handling
    }
  },

  setSelectedEndpoint: (endpoint) => {
    set({ selectedEndpoint: endpoint });
  },

  // Requests
  fetchRequests: async () => {
    try {
      const state = get();
      if (state.requestsLoading) {
        return;
      }

      set({ requestsLoading: true, error: null });

      const requests = await api.getRequests();
      
      // If no requests are returned, that's okay - it might be a new system
      // But if requests is null/undefined, that's an error
      if (!requests) {
        throw new Error('Failed to fetch requests');
      }

      set({ 
        requests,
        requestsLoading: false,
        error: null
      });
    } catch (error) {
      console.error('Error fetching requests:', error);
      set({ 
        error: error.message || 'Failed to fetch requests',
        requestsLoading: false
      });
      throw error; // Propagate error to allow component-level handling
    }
  },

  createRequest: async (data) => {
    try {
      set({ requestsLoading: true, error: null });
      const newRequest = await api.createRequest(data);
      set(state => ({
        requests: [...state.requests, newRequest],
        requestsLoading: false,
        error: null
      }));
      return newRequest;
    } catch (error) {
      console.error('Error creating request:', error);
      set({ 
        error: error.message || 'Failed to create request',
        requestsLoading: false
      });
      throw error; // Propagate error to allow component-level handling
    }
  },

  updateRequest: async (id, data) => {
    try {
      set({ requestsLoading: true, error: null });
      const updatedRequest = await api.updateRequest(id, data);
      set(state => ({
        requests: state.requests.map(request =>
          request.id === id ? updatedRequest : request
        ),
        requestsLoading: false,
        error: null
      }));
      return updatedRequest;
    } catch (error) {
      console.error('Error updating request:', error);
      set({ 
        error: error.message || 'Failed to update request',
        requestsLoading: false
      });
      throw error; // Propagate error to allow component-level handling
    }
  },

  deleteRequest: async (id) => {
    try {
      set({ requestsLoading: true, error: null });
      await api.deleteRequest(id);
      set(state => ({
        requests: state.requests.filter(request => request.id !== id),
        requestsLoading: false,
        error: null
      }));
    } catch (error) {
      console.error('Error deleting request:', error);
      set({ 
        error: error.message || 'Failed to delete request',
        requestsLoading: false
      });
      throw error; // Propagate error to allow component-level handling
    }
  },

  executeRequest: async (requestData) => {
    try {
      const state = get();
      const endpoint = state.endpoints.find(e => e.id === requestData.endpointId);
      if (!endpoint) {
        throw new Error('Endpoint not found or was deleted');
      }

      const url = new URL(endpoint.url);
      requestData.queryParams.forEach(param => {
        if (param.name && param.value) {
          url.searchParams.append(param.name, param.value);
        }
      });

      const headers = {};
      requestData.headers.forEach(header => {
        if (header.name && header.value) {
          headers[header.name] = header.value;
        }
      });

      const response = await fetch(url.toString(), {
        method: endpoint.method,
        headers: {
          ...headers,
          'Content-Type': requestData.body.contentType
        },
        body: ['GET', 'HEAD'].includes(endpoint.method.toUpperCase())
          ? undefined
          : requestData.body.content
      });

      const responseData = await response.text();
      let formattedData;
      try {
        formattedData = JSON.stringify(JSON.parse(responseData), null, 2);
      } catch {
        formattedData = responseData;
      }

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}: ${response.statusText}`);
      }

      return {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
        body: formattedData
      };
    } catch (error) {
      console.error('Error executing request:', error);
      throw error; // Propagate error to allow component-level handling
    }
  },
}));

export default useStore;
